import { Button, Col, Divider, Form, Input, message, Modal, notification, Row, Select, Spin, Table, Tooltip, Upload, UploadFile, UploadProps } from 'antd';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { IRegrasFormValues } from '../../interface';
import { useGrupoContext } from '../../context/GroupContext';
import { useRegras } from '../../hooks/useRegras';

interface IModalRegra {
    hook: ReturnType<typeof useRegras>;
}

export const ModalRegra = ({ hook }: IModalRegra) => {
    const [form] = Form.useForm<IRegrasFormValues>();

    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [anoReferencia, setAnoReferencia] = useState('');

    const canSendFile = fileList.length > 0 && /^\d{4}$/.test(anoReferencia);

    const {
        editingData,
        isRegraOpen,
        fecharModalGrupo,
    } = useGrupoContext();

    const {
        loadingRegraModal,
        options_categoria,
        postRegras,
        handleUpload,
        updateRegras,
    } = hook;

    useEffect(() => {
        if (isRegraOpen && editingData) {
            form.setFieldsValue({
                ano: editingData.ano,
                itens: editingData.itens && editingData.itens.length > 0
                    ? editingData.itens
                    : [{ nome: '', categoria: '', prefixos: [] }],
            });
        } else if (isRegraOpen && !editingData) {
            form.resetFields();
            form.setFieldsValue({
                itens: [{ nome: '', categoria: '', prefixos: [] }],
            });
        }
    }, [isRegraOpen, editingData, form]);

    const handleFinish = async (values: IRegrasFormValues) => {
        if (editingData?.id) {
            await updateRegras(values);
        } else {
            await postRegras(values);
        }
        form.resetFields();
        fecharModalGrupo();
    };

    const isValidFile = (file: File) => {
        return (
            file.type === 'application/vnd.ms-excel' ||
            file.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.name.toLowerCase().endsWith('.csv')
        );
    };

    const uploadProps: UploadProps = {
        accept: '.xls,.xlsx,.csv',
        multiple: false,
        maxCount: 1,
        fileList,

        beforeUpload(file) {
            if (!isValidFile(file)) {
                notification.error({
                    message: 'Arquivo inválido',
                    description: 'Selecione apenas arquivos .xls, .xlsx ou .csv.',
                });

                return Upload.LIST_IGNORE;
            }

            setFileList([
                {
                    uid: file.uid,
                    name: file.name,
                    status: 'done',
                    originFileObj: file,
                },
            ]);

            return false;
        },

        onRemove() {
            setFileList([]);
        },
    };

    const handleSend = async () => {
        if (!fileList.length) {
            message.warning('Selecione um arquivo.');
            return;
        }

        const file = fileList[0].originFileObj as File;

        const response = await handleUpload(
            file,
            Number(anoReferencia)
        );

        if (!response) return;

        form.setFieldsValue({
            ano: response.ano,
            itens: response.itens,
        });

        setFileList([]);
        setAnoReferencia('');
    };

    return (
        <Modal
            title={editingData ? 'Editar Regra' : 'Nova Regra'}
            open={isRegraOpen}
            onCancel={fecharModalGrupo}
            onOk={() => form.submit()}
            width={'70%'}
            footer={[
                <Button
                    key="back"
                    onClick={() => {
                        form.resetFields();
                        setFileList([]);
                        setAnoReferencia('');
                        fecharModalGrupo();
                    }}
                >
                    Fechar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loadingRegraModal}
                    disabled={loadingRegraModal}
                    style={{ backgroundColor: '#008000', borderColor: '#008000' }}
                    onClick={() => form.submit()}
                >
                    {editingData ? 'Atualizar' : 'Salvar'}
                </Button>
            ]}
        >
            <Spin spinning={loadingRegraModal}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                    preserve={false}
                >
                    <Row
                        gutter={24}
                        align="middle"
                        justify="space-between"
                    >
                        <Col flex="260px">
                            <Form.Item
                                name="ano"
                                label="Ano"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor, informe o ano.',
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Ex: 2020"
                                    maxLength={4}
                                />
                            </Form.Item>
                        </Col>
                        <Col flex="40px" style={{ textAlign: 'center' }}>
                            <Divider
                                type="vertical"
                                style={{
                                    height: 90,
                                    margin: 0,
                                }}
                            />
                        </Col>
                        <Col flex="auto">
                            <Row
                                gutter={16}
                                align="bottom"
                                justify='start'
                            >
                                <Col>
                                    <Upload {...uploadProps}>
                                        <Button
                                            type="primary"
                                            icon={<UploadOutlined />}
                                        >
                                            Importar Planilha
                                        </Button>
                                    </Upload>
                                </Col>
                                {fileList.length > 0 && (
                                    <>
                                        <Col>
                                            <Form.Item
                                                label={
                                                    <span>
                                                        Ano Referência&nbsp;
                                                        <Tooltip title="Informe o ano ao qual pertencem os dados da planilha. Este valor é utilizado apenas durante a importação e não será salvo na regra.">
                                                            <InfoCircleOutlined style={{ color: 'orange' }} />
                                                        </Tooltip>
                                                    </span>
                                                }
                                                required
                                                style={{
                                                    margin: 0
                                                }}
                                            >
                                                <Input
                                                    value={anoReferencia}
                                                    onChange={(e) =>
                                                        setAnoReferencia(
                                                            e.target.value.replace(/\D/g, '')
                                                        )
                                                    }
                                                    placeholder="Ex.: 2025"
                                                    maxLength={4}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col>
                                            <Button
                                                style={{ backgroundColor: '#b67300ff', borderColor: '#b67300ff' }}
                                                type="primary"
                                                icon={<SaveOutlined />}
                                                onClick={() => handleSend()}
                                                loading={loadingRegraModal}
                                                disabled={!canSendFile}
                                            >
                                                Enviar Arquivo
                                            </Button>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        </Col>
                    </Row>
                    <Divider style={{ marginBottom: 20, marginTop: 0 }}>
                        Itens da Regra
                    </Divider>
                    <Form.List name="itens">
                        {(fields, { add, remove }) => (
                            <>
                                <Table
                                    size="small"
                                    pagination={false}
                                    rowKey="key"
                                    dataSource={fields}
                                    bordered
                                    locale={{ emptyText: 'Nenhum item cadastrado.' }}
                                    columns={[
                                        {
                                            title: 'Nome',
                                            render: (_, { name, ...restField }) => (
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'nome']}
                                                    rules={[{ required: true, message: 'Informe o nome' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Input placeholder="Nome" />
                                                </Form.Item>
                                            ),
                                        },
                                        {
                                            title: 'Categoria',
                                            width: 220,
                                            render: (_, { name, ...restField }) => (
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'categoria']}
                                                    rules={[{ required: true, message: 'Selecione a categoria' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Select
                                                        showSearch
                                                        options={options_categoria}
                                                        placeholder="Categoria"
                                                    />
                                                </Form.Item>
                                            ),
                                        },
                                        {
                                            title: 'Prefixos',
                                            width: 300,
                                            render: (_, { name, ...restField }) => (
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'prefixos']}
                                                    rules={[{ required: true, message: 'Informe os prefixos' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Select
                                                        mode="tags"
                                                        tokenSeparators={[',', ' ']}
                                                        placeholder="Digite e pressione Enter"
                                                    />
                                                </Form.Item>
                                            ),
                                        },
                                        {
                                            width: 60,
                                            align: 'center',
                                            render: (_, { name }) => (
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    disabled={fields.length === 1}
                                                    onClick={() => remove(name)}
                                                />
                                            ),
                                        },
                                    ]}
                                />

                                <Button
                                    type="dashed"
                                    icon={<PlusOutlined />}
                                    onClick={() =>
                                        add({
                                            nome: '',
                                            categoria: undefined,
                                            prefixos: [],
                                        })
                                    }
                                    style={{ marginTop: 12 }}
                                >
                                    Adicionar linha
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Form>
            </Spin>
        </Modal>
    );
};
